import { Heading, Inline, Stack, Text, textVariants, vars } from '@etg/wings';
import { CancellationRefundOfferReason } from '@eti/schema-types';
import type { Dispatch, SetStateAction } from 'react';
import { useMemo } from 'react';
import { css } from '@eti/styles';
import { useTranslation } from '@eti/providers';
import {
  cancellationGuarantee,
  cancellationInsideEu,
  cancellationInsuranceCoverGenius,
  cancellationOutsideEu,
} from '@eti/products';
import { price as priceUtil } from '@eti/utils';
import { screenReadersOnlyStyles } from '../../../../../utils/styleUtils';
import CaretButtonWithDrawer from '../../common/CaretButtonWithDrawer';
import type {
  AllButScheduleChangeCancellationOptions,
  CancellationOptionInfoType,
  ProductType,
} from './models';
import TopCancellationOption from './TopCancellationOption';
import ExpirationBadge from './ExpirationBadge';
import RefundOfferDrawer from './RefundOfferDrawer';
import { getSortedCancellationOptions } from './utils/utils';
import CancellationProductDrawer from './CancellationProductDrawer';

const caretButtonInlineStyles = css`
  flex-wrap: wrap-reverse;
  gap: 8px 16px;
  justify-content: space-between;
`;

const dividerStyles = css`
  border-bottom: 1px solid ${vars.colors.divider};
`;

interface CancellationOptionsProps {
  cancellationOptions: AllButScheduleChangeCancellationOptions;
  isExpirationDialogOpen: boolean;
  setIsExpirationDialogOpen: Dispatch<SetStateAction<boolean>>;
}

const CancellationOptions = ({
  cancellationOptions,
  isExpirationDialogOpen,
  setIsExpirationDialogOpen,
}: CancellationOptionsProps) => {
  const { t } = useTranslation();
  const allCancellationOptions: CancellationOptionInfoType[] = useMemo(() => {
    const CANCEL_REASON_TO_CONTENT = {
      [CancellationRefundOfferReason.CancelForAnyReason]: {
        drawerDescription: t('Void.Intro.CancellationPolicy.Description'),
        expiredDescription: t('ManageTrip.CancelForAnyReason.Expired.Text'),
        expiredDialogTitle: t('ManageTrip.CancelForAnyReason.Expired.Dialog.Title'),
        expiredDialogDescription: t('ManageTrip.CancelForAnyReason.Expired.Dialog.Description'),
        title: t('ManageTrip.CancelForAnyReason.Title'),
        description: t('ManageTrip.CancelForAnyReason.Description'),
      },
      [CancellationRefundOfferReason.TicketRules]: {
        drawerDescription: t('Void.Intro.CancellationPolicy.Description'),
        expiredDescription: null,
        expiredDialogTitle: t('ManageTrip.TicketRules.Expired.Dialog.Title'),
        expiredDialogDescription: t('ManageTrip.Void.Expired.Dialog.Description'),
        title: t('ManageTrip.TicketRules.Title'),
        description: t('ManageTrip.TicketRules.Description'),
      },
      [CancellationRefundOfferReason.Void]: {
        drawerDescription: t('Void.Intro.CancellationPolicy.Description'),
        expiredDescription: null,
        expiredDialogTitle: t('ManageTrip.Void.Expired.Dialog.Title'),
        expiredDialogDescription: t('ManageTrip.Void.Expired.Dialog.Description'),
        title: t('ManageTrip.Void.Title'),
        description: t('ManageTrip.Void.Description'),
      },
    };

    const getProductDescription = (productId?: number | null) => {
      switch (productId) {
        case cancellationGuarantee.id:
          return t('ManageTrip.CancellationGuarantee.Top.Description');
        case cancellationInsuranceCoverGenius.id:
          return t('ManageTrip.CancellationProtection.Top.Description');
        case cancellationInsideEu.id:
          return t('ManageTrip.CancellationEu.Top.Description');
        case cancellationOutsideEu.id:
          return t('ManageTrip.CancellationEu.Top.Description');
        default:
          return null;
      }
    };

    const refundOffers = cancellationOptions.refundOffers
      ? cancellationOptions.refundOffers.map((refundOffer) => {
          const refundOfferContent =
            CANCEL_REASON_TO_CONTENT[
              refundOffer.refundOfferReason as keyof typeof CANCEL_REASON_TO_CONTENT
            ];
          return {
            descriptionWhenOnBottom: (
              <>
                <Text aria-hidden asHtml>
                  {t(
                    'ManageTrip.Cancel.EstimatedRefund.Text',
                    priceUtil.formatPrice(refundOffer.refundableAmount),
                  )}
                </Text>
                <Text asHtml className={screenReadersOnlyStyles}>
                  {t(
                    'ManageTrip.Cancel.EstimatedRefund.Text',
                    priceUtil.replaceWithCurrencyName(
                      priceUtil.formatPrice(refundOffer.refundableAmount),
                    ),
                  )}
                </Text>
              </>
            ),
            drawerContent: (
              <RefundOfferDrawer
                description={refundOfferContent.drawerDescription}
                refundOffer={refundOffer}
                title={refundOfferContent.title}
              />
            ),
            expiredDescription: refundOfferContent.expiredDescription,
            expiredDialogTitle: refundOfferContent.expiredDialogTitle,
            expiredDialogDescription: refundOfferContent.expiredDialogDescription,
            reason: refundOffer.refundOfferReason,
            refundOffer,
            title: refundOfferContent.title,
            descriptionWhenOnTop: refundOfferContent.description,
          };
        })
      : [];

    const cancellationProducts = cancellationOptions.includedCancellationProducts.map(
      (product: ProductType) => {
        const productDescription = getProductDescription(Number(product.id));
        return {
          descriptionWhenOnBottom: <Text asHtml>{productDescription}</Text>,
          drawerContent: (
            <CancellationProductDrawer
              description={product.texts.upsellTextWhenBooked || ''}
              title={product.name}
            />
          ),
          expiredDescription: null,
          expiredDialogTitle: null,
          expiredDialogDescription: null,
          reason: product.code,
          refundOffer: null,
          title: product.name,
          descriptionWhenOnTop: productDescription,
        };
      },
    );
    return getSortedCancellationOptions([...refundOffers, ...cancellationProducts]);
  }, [cancellationOptions.includedCancellationProducts, cancellationOptions.refundOffers, t]);

  const topCancellationOption = allCancellationOptions[0];
  const bottomCancellationOptions = allCancellationOptions.slice(1);

  return (
    <Stack
      aria-labelledby="cancel-button"
      id="tab-panel-cancel-button"
      role="tabpanel"
      spacing={24}
    >
      <TopCancellationOption
        cancellationOption={topCancellationOption}
        isBestOption={
          allCancellationOptions.length > 1 &&
          topCancellationOption.refundOffer?.refundOfferReason ===
            CancellationRefundOfferReason.Void
        }
        isExpirationDialogOpen={isExpirationDialogOpen}
        setIsExpirationDialogOpen={setIsExpirationDialogOpen}
      />
      {bottomCancellationOptions.length > 0 && (
        <Stack>
          <div className={dividerStyles} />
          <Heading level={3}>{t('ManageTrip.Cancel.BoughtProducts.Title')}</Heading>
          <Stack role="list" spacing={8}>
            {bottomCancellationOptions.map((cancellationOption) => (
              <div key={cancellationOption.reason} role="listitem">
                <CaretButtonWithDrawer
                  dataTestId={`${cancellationOption.reason}-button`}
                  drawerContent={cancellationOption.drawerContent}
                  drawerTitle={t('ManageTrip.Cancel.TopOption.Button.Text')}
                  isDisabled={Boolean(cancellationOption.refundOffer?.expirationTimeInSec === 0)}
                  key={cancellationOption.reason}
                  snowplow={{
                    category: 'Manage Trip',
                    label: cancellationOption.reason,
                  }}
                >
                  <Inline alignY="center" className={caretButtonInlineStyles}>
                    <Stack align="start" spacing={4}>
                      <Text variant={textVariants._16_BOLD}>{cancellationOption.title}</Text>
                      {cancellationOption.refundOffer?.expirationTimeInSec === 0 ? (
                        <Text asHtml>{cancellationOption.expiredDescription}</Text>
                      ) : (
                        cancellationOption.descriptionWhenOnBottom
                      )}
                    </Stack>
                    {cancellationOption.refundOffer !== null && (
                      <ExpirationBadge
                        dataTestId={`${cancellationOption.reason}-expiration-badge`}
                        expires={cancellationOption.refundOffer.expirationTimeInSec}
                      />
                    )}
                  </Inline>
                </CaretButtonWithDrawer>
              </div>
            ))}
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};

export default CancellationOptions;
